import * as React from 'react';
import { useRef } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';

import Button from '../../core/components/Button.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import FormField from '../../core/components/FormField.tsx';
import SettingsFormCard from '../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import ClientLookupInput from '../ClientLookupInput.tsx';

import { FormRow } from './FormsConfig.tsx';
import { CREATE_SELF_SERVICE_FORM_CLIENT } from './queries.ts';

interface ClientFormData {
    clientCustomerNumber: string;
    clientName: string;
}

const DEFAULT_VALUES: ClientFormData = {
    clientCustomerNumber: '',
    clientName: '',
};

interface ClientCreateCardProps {
    form: FormRow;
    clientLookupDefinitionId: string | null;
    onClose: () => void;
    onChanged: () => Promise<void>;
}

const ClientCreateCard = ({ form, clientLookupDefinitionId, onClose, onChanged }: ClientCreateCardProps) => {
    const { t } = useTranslation('forms');
    const { publishToast } = useToaster();

    const [createClient] = useMutation(CREATE_SELF_SERVICE_FORM_CLIENT);

    const selectedClientDataRef = useRef<Record<string, unknown> | null>(null);

    const clientForm = useForm<ClientFormData>({ defaultValues: DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = clientForm.formState.errors;

    const handleSubmit = async (formData: ClientFormData) => {
        try {
            await createClient({
                variables: {
                    formId: form.id,
                    clientCustomerNumber: formData.clientCustomerNumber.trim(),
                    clientName: formData.clientName.trim(),
                    clientData: selectedClientDataRef.current || {},
                },
            });
            selectedClientDataRef.current = null;
            await onChanged();
            publishToast({ description: t('tenant.clients.toast.createSuccess'), status: 'success' });
            onClose();
        } catch (e) {
            console.error(e);
            publishToast({ description: t('tenant.clients.toast.createError'), status: 'error' });
        }
    };

    return (
        <SettingsFormCard
            onSubmit={clientForm.handleSubmit(handleSubmit)}
            title={t('tenant.clients.create.title')}
            description={t('tenant.clients.create.description', { form: form.name || t('tenant.forms.unnamed') })}
        >
            <Controller
                name="clientCustomerNumber"
                control={clientForm.control}
                rules={{ required: t('tenant.errors.missingCustomerNumber') }}
                render={({ field }) => (
                    <FormField
                        label={t('tenant.shared.customer')}
                        required
                        error={formErrors.clientCustomerNumber}
                        helpText={
                            clientLookupDefinitionId
                                ? t('tenant.shared.customerHelp')
                                : t('tenant.shared.customerHelpNoMasterData')
                        }
                    >
                        <ClientLookupInput
                            clientLookupDefinitionId={clientLookupDefinitionId}
                            value={field.value}
                            onValueChange={field.onChange}
                            onClientSelect={({ customerNumber, companyName, entity }) => {
                                selectedClientDataRef.current = entity;
                                field.onChange(customerNumber);
                                clientForm.setValue('clientName', companyName);
                            }}
                        />
                    </FormField>
                )}
            />

            <Controller
                name="clientName"
                control={clientForm.control}
                render={({ field }) => (
                    <FormField label={t('tenant.shared.customerName')} helpText={t('tenant.shared.customerNameHelp')}>
                        <StringField {...field} />
                    </FormField>
                )}
            />

            <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={onClose}>
                    {t('tenant.shared.cancel')}
                </Button>
                <Button type="submit" variant="primary">
                    {t('tenant.clients.create.submit')}
                </Button>
            </div>
        </SettingsFormCard>
    );
};

export default ClientCreateCard;
